import { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import * as api from './ddosguardApi';
import { AuthContext } from '_store';
import { apiBase } from 'api/client';
import { Modal, useActivityFrame } from '_components';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import brandLogoSrc from 'res/logo.svg';
import { ThemeContext } from 'theme';
import {
    applyFilterAddEvent,
    compareActiveFiltersStable,
    applyFilterRemoveEvent,
    filterKeyOf,
    reconcileRestFilters,
    reconcileStatusFilters,
} from './ddosguardState';
import './ddosguard.css';

export { DdosGuard };

// ── Helpers ────────────────────────────────────────────────────────

function formatPps(n) {
    if (n == null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return `${parseFloat(n.toFixed(1))}`;
}

function formatBps(n) {
    if (n == null) return '—';
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} Gbps`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} Mbps`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)} kbps`;
    return `${parseFloat(n.toFixed(1))} bps`;
}

function timeAgo(iso) {
    if (!iso) return '—';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatTimeShort(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDurationMinSec(value) {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const totalSeconds = Math.max(0, Math.round(Number(value)));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
}

function formatFilterReason(reason) {
    if (reason == null) return '—';
    const raw = String(reason).trim();
    if (!raw) return '—';

    const normalized = raw.toLowerCase();
    const hasPps = /\bpps\b|packet(?:s)?\s+per\s+second|packet\s+rate/.test(normalized);
    const hasBps = /\bbps\b|bit(?:s)?\s+per\s+second|bit\s+rate/.test(normalized);

    if (hasPps && hasBps) return 'Packet and bit-rate thresholds exceeded';
    if (hasPps) return 'Packet-per-second threshold exceeded';
    if (hasBps) return 'Bits-per-second threshold exceeded';
    if (/multi[-\s]?sketch/.test(normalized)) return 'Anomaly detected by multi-sketch analysis';

    return raw;
}

function formatCount(n) {
    if (n == null || Number.isNaN(Number(n))) return '—';
    return new Intl.NumberFormat().format(Math.round(Number(n)));
}

function formatBytes(n) {
    if (n == null || Number.isNaN(Number(n))) return '—';
    const value = Number(n);
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let unitIndex = 0;
    let normalized = value;
    while (normalized >= 1024 && unitIndex < units.length - 1) {
        normalized /= 1024;
        unitIndex += 1;
    }
    const rounded = normalized >= 100 ? normalized.toFixed(0) : normalized.toFixed(1);
    return `${rounded} ${units[unitIndex]}`;
}

function stateClass(state) {
    if (!state) return 'idle';
    return state.toLowerCase().replace(/_/g, '-');
}

function normalizeTelemetryStatus(status) {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'online') return 'online';
    if (normalized === 'stale') return 'stale';
    if (normalized === 'offline') return 'offline';
    if (normalized === 'no-data') return 'no-data';
    return 'no-data';
}

function telemetryStatusLabel(status) {
    const normalized = normalizeTelemetryStatus(status);
    if (normalized === 'online') return 'Online';
    if (normalized === 'stale') return 'Stale';
    if (normalized === 'offline') return 'Offline';
    return 'No data';
}

function telemetryStatusMessage(status, lastSeen) {
    const normalized = normalizeTelemetryStatus(status);

    if (normalized === 'online') return null;
    if (normalized === 'stale') {
        if (lastSeen) return `Rule telemetry is stale. Last update ${timeAgo(lastSeen)}.`;
        return 'Rule telemetry is stale. Values may be outdated.';
    }
    if (normalized === 'offline') {
        return 'Rule telemetry is offline. DDoS Guard rules are currently down for this router.';
    }
    return 'No rule telemetry data is available for this router yet.';
}

function formatPercent(n) {
    if (n == null || Number.isNaN(n)) return '—';
    if (n >= 100) return `${Math.round(n)}%`;
    return `${n.toFixed(1)}%`;
}

function eventUiKeyOf(ev, fallbackIndex = 0) {
    if (ev?._uiKey) return ev._uiKey;
    if (ev?.id != null) return `db-${ev.id}-${ev.occurred_at ?? 'na'}`;
    return `evt-${ev?.router_id ?? 'r'}-${ev?.rule_id ?? 'u'}-${ev?.event_type ?? 'e'}-${ev?.occurred_at ?? 't'}-${ev?.filter_id ?? 'f'}-${fallbackIndex}`;
}

function filterLogEntryEventIds(entry) {
    return [...new Set([
        entry?.add_event_id,
        entry?.remove_event_id,
    ]
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0))];
}

function filterLogEntryKeyOf(entry) {
    const eventIds = filterLogEntryEventIds(entry).join('-');

    return [
        entry?.router_id ?? 'router',
        entry?.rule_id ?? 'rule',
        entry?.filter_id ?? 'filter',
        entry?.added_at ?? 'no-add',
        entry?.removed_at ?? 'no-remove',
        eventIds || 'no-events',
    ].join('|');
}

function normalizeEventRows(rawEvents, { maxTotal = 0, maxPerRule = 0 } = {}) {
    const uniqueByKey = new Map();

    (Array.isArray(rawEvents) ? rawEvents : []).forEach((ev) => {
        if (!ev) return;

        const dedupeKey = ev.id != null
            ? `id:${ev.id}`
            : `evt:${ev.router_id ?? 'r'}|${ev.rule_id ?? 'u'}|${ev.event_type ?? 'e'}|${ev.filter_id ?? 'f'}|${ev.occurred_at ?? 't'}|${ev.state ?? 's'}|${ev.pattern ?? 'p'}`;

        if (!uniqueByKey.has(dedupeKey)) {
            uniqueByKey.set(dedupeKey, ev);
        }
    });

    const sorted = [...uniqueByKey.values()].sort((a, b) => {
        const aTime = Date.parse(a?.occurred_at || '');
        const bTime = Date.parse(b?.occurred_at || '');
        const aMs = Number.isFinite(aTime) ? aTime : 0;
        const bMs = Number.isFinite(bTime) ? bTime : 0;
        return bMs - aMs;
    });

    const perRuleCounts = new Map();
    const limited = [];

    for (const ev of sorted) {
        const ruleBucket = `${ev?.router_id ?? 'r'}|${ev?.rule_id ?? 'u'}`;
        const seen = perRuleCounts.get(ruleBucket) || 0;

        if (maxPerRule > 0 && seen >= maxPerRule) continue;

        perRuleCounts.set(ruleBucket, seen + 1);
        limited.push(ev);

        if (maxTotal > 0 && limited.length >= maxTotal) break;
    }

    return limited.map((ev, index) => ({
        ...ev,
        _uiKey: eventUiKeyOf(ev, index),
    }));
}

// ── Pattern display component ───────────────────────────────────────

function parsePattern(raw) {
    if (!raw || raw === '—') return null;
    const pairs = {};
    const tokens = raw.trim().split(/\s+/).filter(Boolean);
    let pendingKey = null;

    const knownKeys = new Set([
        'family',
        'proto',
        'src',
        'dst',
        'dport',
        'sport',
        'ipflags',
        'tcpflags',
        'udpflags',
    ]);

    const knownProtocols = new Set([
        'tcp',
        'udp',
        'icmp',
        'icmpv6',
        'sctp',
        'gre',
        'esp',
        'ah',
    ]);

    for (const token of tokens) {
        const normalized = String(token).trim();
        if (!normalized) continue;

        const eq = normalized.indexOf('=');
        if (eq >= 0) {
            const key = normalized.slice(0, eq).toLowerCase();
            const value = normalized.slice(eq + 1);
            if (key && value) pairs[key] = value;
            pendingKey = null;
            continue;
        }

        const colon = normalized.indexOf(':');
        if (colon > 0 && colon < normalized.length - 1) {
            const key = normalized.slice(0, colon).toLowerCase();
            const value = normalized.slice(colon + 1);
            if (knownKeys.has(key) && value) {
                pairs[key] = value;
                pendingKey = null;
                continue;
            }
        }

        const lowered = normalized.toLowerCase();

        if (pendingKey && !knownKeys.has(lowered)) {
            pairs[pendingKey] = normalized;
            pendingKey = null;
            continue;
        }

        if (knownKeys.has(lowered)) {
            pendingKey = lowered;
            continue;
        }

        if (!pairs.family && /^ipv[46]$/.test(lowered)) {
            pairs.family = lowered;
            continue;
        }

        if (!pairs.proto && knownProtocols.has(lowered)) {
            pairs.proto = lowered;
            continue;
        }
    }

    return Object.keys(pairs).length ? pairs : null;
}

function formatIpFlags(raw) {
    const value = String(raw || '').trim();
    if (!value) return null;

    const parsed = /^0x/i.test(value)
        ? Number.parseInt(value.slice(2), 16)
        : Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) return value;

    const names = [];
    if (parsed & 0x04) names.push('RSV');
    if (parsed & 0x02) names.push('DF');
    if (parsed & 0x01) names.push('MF');

    const hex = `0x${parsed.toString(16).padStart(2, '0')}`;
    return names.length ? `${names.join('+')} (${hex})` : hex;
}

function PatternDisplay({ pattern }) {
    if (!pattern || pattern === '—') return <span>—</span>;
    const p = parsePattern(pattern);
    if (!p) return <span className="ddos-td-mono">{pattern}</span>;

    const { family, proto, src, dst, dport, sport, ipflags, tcpflags, udpflags } = p;
    const flags = [
        tcpflags ? `tcpflags:${tcpflags}` : null,
        udpflags ? `udpflags:${udpflags}` : null,
        ipflags ? `ipflags:${formatIpFlags(ipflags)}` : null,
    ].filter(Boolean).join(' · ');
    const sourceEndpoint = src ? (sport ? `${src}:${sport}` : src) : (sport ? `:${sport}` : null);
    const destinationEndpoint = dst ? (dport ? `${dst}:${dport}` : dst) : (dport ? `:${dport}` : null);
    const bothSides = sourceEndpoint && destinationEndpoint;
    const showSourceLabel = !bothSides || !src;
    const showDestinationLabel = !bothSides || !dst;

    return (
        <span className="ddos-pattern">
            {family && <span className="ddos-pattern-proto">{family.toUpperCase()}</span>}
            {proto  && <span className="ddos-pattern-proto">{proto.toUpperCase()}</span>}
            {(sourceEndpoint || destinationEndpoint) && (
                <span className="ddos-pattern-flow">
                    {sourceEndpoint && (
                        <span className="ddos-pattern-labeled-addr">
                            {showSourceLabel && <span className="ddos-pattern-dir-label">src</span>}
                            <span className="ddos-pattern-addr">{sourceEndpoint}</span>
                        </span>
                    )}
                    {bothSides && <span className="ddos-pattern-arrow">→</span>}
                    {destinationEndpoint && (
                        <span className="ddos-pattern-labeled-addr">
                            {showDestinationLabel && <span className="ddos-pattern-dir-label">dst</span>}
                            <span className="ddos-pattern-addr">{destinationEndpoint}</span>
                        </span>
                    )}
                </span>
            )}
            {flags && <span className="ddos-pattern-flags">{flags}</span>}
        </span>
    );
}

const REPORT_SUMMARY_FIELDS = [
    { key: 'samples', label: 'Samples' },
    { key: 'max_current_pps', label: 'Max PPS' },
    { key: 'avg_current_pps', label: 'Avg PPS' },
    { key: 'max_current_bps', label: 'Max BPS' },
    { key: 'avg_current_bps', label: 'Avg BPS' },
    { key: 'dropped_pkts_total', label: 'Filtered Packets' },
    { key: 'dropped_bytes_total', label: 'Filtered Bytes' },
];

const REPORT_SUMMARY_LABELS = Object.fromEntries(
    REPORT_SUMMARY_FIELDS.map(field => [field.key, field.label]),
);

const REPORT_SUMMARY_GROUPS = [
    {
        key: 'traffic',
        title: 'Traffic',
        fields: ['samples', 'max_current_pps', 'avg_current_pps', 'max_current_bps', 'avg_current_bps'],
    },
    {
        key: 'filtered',
        title: 'Filtered',
        fields: ['dropped_pkts_total', 'dropped_bytes_total'],
    },
];

const REPORT_SERIES_KEYS = [
    'total_pps',
    'total_bps',
    'threshold_pps',
    'threshold_bps',
    'current_pps',
    'current_bps',
    'peak_pps',
    'peak_bps',
];

const REPORT_COMBINED_CHARTS = [
    {
        key: 'pps',
        title: 'PPS',
        lines: [
            { key: 'total_pps', label: 'Total', color: 'var(--color-chart-total)' },
            { key: 'threshold_pps', label: 'Threshold', color: 'var(--color-chart-threshold)' },
            { key: 'current_pps', label: 'Filtered', color: 'var(--color-chart-filtered)', fillOpacity: 0.2 },
        ],
    },
    {
        key: 'bps',
        title: 'BPS',
        lines: [
            { key: 'total_bps', label: 'Total', color: 'var(--color-chart-total)' },
            { key: 'threshold_bps', label: 'Threshold', color: 'var(--color-chart-threshold)' },
            { key: 'current_bps', label: 'Filtered', color: 'var(--color-chart-filtered)', fillOpacity: 0.2 },
        ],
    },
];

function formatReportMetric(metricKey, value) {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const n = Number(value);
    if (metricKey === 'dropped_pkts') return `${formatPps(n)} pps`;
    if (metricKey === 'dropped_bits') return formatBps(n);
    if (metricKey.includes('_pps')) return `${formatPps(n)} pps`;
    if (metricKey.includes('_bps')) return formatBps(n);
    if (metricKey.includes('bytes')) return formatBytes(n);
    return formatCount(n);
}

function csvEscape(value) {
    if (value == null) return '';
    const s = String(value);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function buildSeriesRows(seriesMap, keys) {
    const byTimestamp = new Map();

    keys.forEach((key) => {
        let arr = Array.isArray(seriesMap?.[key]) ? seriesMap[key] : [];

        // Backward compatibility: some API versions return dropped bytes instead of dropped bits.
        if (!arr.length && key === 'dropped_bits' && Array.isArray(seriesMap?.dropped_bytes)) {
            arr = seriesMap.dropped_bytes.map(sample => ({
                ...sample,
                value: Number(sample?.value) * 8,
            }));
        }

        arr.forEach((sample) => {
            const timestamp = sample?.timestamp;
            const value = Number(sample?.value);
            if (!timestamp || !Number.isFinite(value)) return;

            if (!byTimestamp.has(timestamp)) byTimestamp.set(timestamp, { timestamp });
            byTimestamp.get(timestamp)[key] = value;
        });
    });

    return [...byTimestamp.values()].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

function buildFilterReportCsv(report) {
    const lines = [];
    const session = report?.session || {};
    const summary = report?.summary || {};
    const reportEvents = Array.isArray(report?.events) ? report.events : [];
    const reportSeries = report?.series || {};

    lines.push('section,field,value');
    lines.push(`meta,router_id,${csvEscape(report?.router_id)}`);
    lines.push(`meta,rule_id,${csvEscape(report?.rule_id)}`);
    lines.push(`meta,filter_id,${csvEscape(report?.filter_id)}`);
    lines.push(`session,started_at,${csvEscape(session.started_at)}`);
    lines.push(`session,ended_at,${csvEscape(session.ended_at)}`);
    lines.push(`session,duration_seconds,${csvEscape(session.duration_seconds)}`);
    lines.push(`session,pattern,${csvEscape(session.pattern)}`);
    lines.push(`session,reason,${csvEscape(session.reason)}`);

    REPORT_SUMMARY_FIELDS.forEach((field) => {
        lines.push(`summary,${field.key},${csvEscape(summary[field.key])}`);
    });

    lines.push('');
    lines.push('events_timestamp,event_type,state,filter_id,pattern,reason');
    reportEvents.forEach((ev) => {
        lines.push([
            ev?.occurred_at,
            ev?.event_type,
            ev?.state,
            ev?.filter_id,
            ev?.pattern,
            ev?.reason,
        ].map(csvEscape).join(','));
    });

    lines.push('');
    lines.push(`series_timestamp,${REPORT_SERIES_KEYS.join(',')}`);
    buildSeriesRows(reportSeries, REPORT_SERIES_KEYS).forEach((row) => {
        lines.push([
            row.timestamp,
            ...REPORT_SERIES_KEYS.map(k => row[k] ?? ''),
        ].map(csvEscape).join(','));
    });

    return lines.join('\n');
}

function downloadTextFile(filename, content, mimeType = 'text/plain;charset=utf-8') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function waitForDomPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
        });
    });
}

function waitForImagesLoaded(root) {
    if (!root) return Promise.resolve();
    const images = Array.from(root.querySelectorAll('img'));
    if (!images.length) return Promise.resolve();

    return Promise.all(images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
            };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
        });
    }));
}

function waitForFontsLoaded(root) {
    const fonts = root?.ownerDocument?.fonts;
    if (!fonts?.ready) return Promise.resolve();
    return fonts.ready.catch(() => undefined);
}

function collectPdfChartImages(sourceCard) {
    if (!sourceCard) return [];
    const serializer = new XMLSerializer();

    return Array.from(sourceCard.querySelectorAll('.ddos-report-chart-canvas')).map((chartCanvas) => {
        const svg = chartCanvas.querySelector('svg');
        if (!svg) return null;

        const svgClone = svg.cloneNode(true);
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

        const rect = svg.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width || Number(svg.getAttribute('width')) || 0));
        const height = Math.max(1, Math.round(rect.height || Number(svg.getAttribute('height')) || 0));
        svgClone.setAttribute('width', String(width));
        svgClone.setAttribute('height', String(height));

        const lightPdfColors = {
            'var(--color-chart-total)': '#0f9faf',
            'var(--color-chart-threshold)': '#f59e0b',
            'var(--color-chart-filtered)': '#ba1a1a',
            'var(--color-report-chart-grid)': '#cccccc',
            'var(--color-chart-label)': '#282939',
            'var(--color-chart-active-dot)': '#ffffff',
            'var(--color-chart-grid)': '#e5e7eb',
            'var(--color-text-muted)': '#47464c',
            'var(--color-text-strong)': '#131524',
            'var(--color-text-main)': '#181c1d',
            'var(--color-surface-card)': '#ffffff',
            'var(--color-outline-soft)': '#c8c5cd',
        };
        let markup = serializer.serializeToString(svgClone);
        Object.entries(lightPdfColors).forEach(([token, value]) => {
            markup = markup.replaceAll(token, value);
        });
        return {
            src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`,
            width,
            height,
        };
    });
}

function normalizePdfTextContent(root) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const replacements = [
        [/→/g, '->'],
    ];

    while (walker.nextNode()) {
        const node = walker.currentNode;
        let text = node.nodeValue || '';
        replacements.forEach(([pattern, replacement]) => {
            text = text.replace(pattern, replacement);
        });
        node.nodeValue = text;
    }
}

function renderEmptyEventsRow(tbody) {
    if (!tbody) return;
    const tr = document.createElement('tr');
    tr.className = 'ddos-tr';
    const td = document.createElement('td');
    td.className = 'ddos-td';
    td.colSpan = 4;
    td.textContent = 'No report events';
    tr.appendChild(td);
    tbody.appendChild(tr);
}

function buildPdfReportFrame({ sourceCard, descriptor, report, exportedAtText, pageIndex, totalPages }) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ddos-pdf-page-frame';
    wrapper.dataset.theme = 'light';

    const brand = document.createElement('div');
    brand.className = 'ddos-pdf-brand';

    const brandLeft = document.createElement('div');
    brandLeft.className = 'ddos-pdf-brand-left';

    const logo = document.createElement('img');
    logo.className = 'ddos-pdf-brand-logo';
    logo.src = brandLogoSrc;
    logo.alt = 'NetX';
    logo.decoding = 'sync';

    const brandText = document.createElement('div');
    brandText.className = 'ddos-pdf-brand-text';

    const brandTitle = document.createElement('div');
    brandTitle.className = 'ddos-pdf-brand-title';
    brandTitle.textContent = 'DDoS Guard Report';

    const brandMeta = document.createElement('div');
    brandMeta.className = 'ddos-pdf-brand-meta';
    brandMeta.textContent = `Router ${report?.router_id ?? '—'} · Rule ${report?.rule_id ?? '—'} · Filter #${report?.filter_id ?? '—'}`;

    const brandRight = document.createElement('div');
    brandRight.className = 'ddos-pdf-brand-right';

    const brandStamp = document.createElement('span');
    brandStamp.className = 'ddos-pdf-brand-stamp';
    brandStamp.textContent = `Exported ${exportedAtText}`;

    const brandPage = document.createElement('span');
    brandPage.className = 'ddos-pdf-brand-page';
    brandPage.textContent = `Page ${pageIndex + 1} of ${totalPages}`;

    brandText.append(brandTitle, brandMeta);
    brandLeft.append(logo, brandText);
    brandRight.append(brandStamp, brandPage);
    brand.append(brandLeft, brandRight);

    const cardClone = sourceCard.cloneNode(true);
    cardClone.classList.add('ddos-pdf-page-card');

    const headActions = cardClone.querySelector('.ddos-report-head-right');
    if (headActions) headActions.remove();

    const body = cardClone.querySelector('.ddos-report-body');
    if (body) {
        const sectionTitles = Array.from(body.querySelectorAll('.ddos-report-section-title'));
        const [filterTitle, summaryTitle, trafficTitle, eventTitle] = sectionTitles;
        const filterGrid = body.querySelector('.ddos-report-filter-grid');
        const patternCard = body.querySelector('.ddos-report-pattern-card');
        const summaryLayout = body.querySelector('.ddos-report-summary-layout');
        const chartGrid = body.querySelector('.ddos-report-chart-grid');
        const eventWrap = body.querySelector('.ddos-table-wrap');

        if (descriptor.type === 'overview') {
            const nextNodes = [
                filterTitle,
                filterGrid,
                patternCard,
                summaryTitle,
                summaryLayout,
                trafficTitle,
                chartGrid,
            ]
                .filter(Boolean)
                .map(node => node.cloneNode(true));
            body.replaceChildren(...nextNodes);

            if (Array.isArray(descriptor.chartImages) && descriptor.chartImages.length) {
                const clonedCanvases = Array.from(body.querySelectorAll('.ddos-report-chart-canvas'));
                clonedCanvases.forEach((canvasNode, index) => {
                    const chartImage = descriptor.chartImages[index];
                    if (!chartImage?.src) return;

                    canvasNode.replaceChildren();
                    const img = document.createElement('img');
                    img.className = 'ddos-pdf-chart-image';
                    img.src = chartImage.src;
                    img.alt = 'Report chart';
                    img.decoding = 'sync';
                    canvasNode.appendChild(img);
                });
            }
        } else {
            const titleNode = eventTitle ? eventTitle.cloneNode(true) : document.createElement('h5');
            titleNode.className = 'ddos-report-section-title';
            titleNode.textContent = descriptor.title || 'Event Timeline';

            const tableWrap = eventWrap ? eventWrap.cloneNode(true) : document.createElement('div');
            tableWrap.classList.add('ddos-table-wrap');
            const tbody = tableWrap.querySelector('tbody');
            if (tbody) {
                tbody.replaceChildren();
                if (Array.isArray(descriptor.rows) && descriptor.rows.length > 0) {
                    descriptor.rows.forEach((row) => {
                        tbody.appendChild(row.cloneNode(true));
                    });
                } else {
                    renderEmptyEventsRow(tbody);
                }
            }

            const nodes = [titleNode, tableWrap];
            if (descriptor.moreCount > 0) {
                const note = document.createElement('div');
                note.className = 'ddos-pdf-note';
                note.textContent = `Showing first ${descriptor.shownCount} of ${descriptor.totalCount} events. Export CSV/JSON for full timeline.`;
                nodes.push(note);
            }

            body.replaceChildren(...nodes);
        }
    }

    normalizePdfTextContent(cardClone);

    const footer = document.createElement('div');
    footer.className = 'ddos-pdf-footer';

    const footerLeft = document.createElement('span');
    footerLeft.textContent = 'Stats Dashboard';

    const footerRight = document.createElement('span');
    footerRight.textContent = `Page ${pageIndex + 1} of ${totalPages}`;

    footer.append(footerLeft, footerRight);
    wrapper.append(brand, cardClone, footer);

    return wrapper;
}

function CombinedSeriesChart({ title, lines, reportSeries }) {
    const rows = buildSeriesRows(reportSeries, lines.map(line => line.key));

    if (!rows.length) {
        return (
            <div className="ddos-report-chart-card">
                <div className="ddos-report-chart-head">
                    <h5 className="ddos-report-chart-title">{title}</h5>
                </div>
                <div className="ddos-report-chart-empty">No series data</div>
            </div>
        );
    }

    const values = rows.flatMap((row) =>
        lines
            .map(line => Number(row[line.key]))
            .filter(v => Number.isFinite(v)),
    );

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const metricKey = lines[0]?.key || 'current_pps';

    const labelByKey = Object.fromEntries(lines.map(lineDef => [lineDef.key, lineDef.label]));

    const tooltipFormatter = (value, name, item) => {
        const dataKey = item?.dataKey;
        const label = labelByKey[dataKey] || name || dataKey;
        return [formatReportMetric(dataKey, value), label];
    };

    const first = rows[0];
    const last = rows[rows.length - 1];

    return (
        <div className="ddos-report-chart-card">
            <div className="ddos-report-chart-head">
                <h5 className="ddos-report-chart-title">{title}</h5>
                <div className="ddos-report-chart-legend">
                    {lines.map((lineDef) => (
                        <span
                            key={lineDef.key}
                            className="ddos-report-legend-item"
                            title={lineDef.label}
                            aria-label={lineDef.label}
                        >
                            <span className="ddos-report-legend-swatch" style={{ '--legend-color': lineDef.color }} />
                            <span className="ddos-report-legend-text">{lineDef.label}</span>
                        </span>
                    ))}
                </div>
            </div>
            <div className="ddos-report-chart-canvas">
                <ResponsiveContainer width="100%" height={196}>
                    <AreaChart
                        data={rows}
                        margin={{ top: 8, right: 10, left: 6, bottom: 0 }}
                    >
                        <CartesianGrid stroke="var(--color-report-chart-grid)" strokeDasharray="3 4" vertical={false} />
                        <XAxis
                            dataKey="timestamp"
                            tickFormatter={formatTimeShort}
                            minTickGap={24}
                            tick={{ fill: 'var(--color-text-muted)', fontSize: 11, fontWeight: 600 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={(value) => formatReportMetric(metricKey, value)}
                            tick={{ fill: 'var(--color-text-muted)', fontSize: 10, fontWeight: 600 }}
                            width={112}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            labelFormatter={formatTime}
                            formatter={tooltipFormatter}
                            contentStyle={{
                                border: '1px solid var(--color-outline-soft)',
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--color-surface-card)',
                                boxShadow: 'var(--shadow-panel)',
                            }}
                            itemStyle={{ color: 'var(--color-text-strong)', fontWeight: 600, fontSize: '12px' }}
                            labelStyle={{ color: 'var(--color-chart-label)', fontWeight: 800, fontSize: '12px' }}
                        />
                        {lines.map((lineDef) => (
                            <Area
                                key={lineDef.key}
                                type="monotone"
                                dataKey={lineDef.key}
                                name={lineDef.label}
                                stroke={lineDef.color}
                                strokeWidth={2.5}
                                fill={lineDef.color}
                                fillOpacity={lineDef.fillOpacity ?? 0}
                                dot={false}
                                activeDot={{ r: 4, stroke: 'var(--color-chart-active-dot)', strokeWidth: 2 }}
                                connectNulls
                                isAnimationActive={false}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <div className="ddos-report-chart-foot">
                <span>{formatTimeShort(first.timestamp)}</span>
                <span>
                    Range {formatReportMetric(metricKey, minVal)} - {formatReportMetric(metricKey, maxVal)}
                </span>
                <span>{formatTimeShort(last.timestamp)}</span>
            </div>
        </div>
    );
}

function FilterReportPanel({
    report,
    loading,
    error,
    onExportJson,
    onExportCsv,
    onExportPdf,
    onDeleteEntry,
    pdfExporting = false,
    deletingEntry = false,
    inModal = false,
    rootRef = null,
}) {
    if (!loading && !error && !report) return null;

    const session = report?.session || {};
    const summary = report?.summary || {};
    const reportEvents = Array.isArray(report?.events) ? report.events : [];
    const reportSeries = report?.series || {};

    return (
        <div ref={rootRef} className={`ddos-report-card${inModal ? ' in-modal' : ''}`}>
            <div className="ddos-report-head">
                <div className="ddos-report-head-left">
                    {!inModal && <h4 className="ddos-report-title">Filter Report</h4>}
                    {!loading && !error && report && (
                        <p className="ddos-report-subtitle">
                            Router {report.router_id} · Rule {report.rule_id} · Filter #{report.filter_id}
                        </p>
                    )}
                </div>
                <div className="ddos-report-head-right">
                    <button
                        className="ddos-log-btn secondary"
                        onClick={onExportJson}
                        disabled={!report || loading || pdfExporting}
                    >
                        Export JSON
                    </button>
                    <button
                        className="ddos-log-btn"
                        onClick={onExportCsv}
                        disabled={!report || loading || pdfExporting}
                    >
                        Export CSV
                    </button>
                    <button
                        className="ddos-log-btn"
                        onClick={onExportPdf}
                        disabled={!report || loading || pdfExporting}
                    >
                        {pdfExporting ? 'Exporting PDF...' : 'Export PDF'}
                    </button>
                    {onDeleteEntry && (
                        <button
                            className="ddos-log-btn danger"
                            onClick={onDeleteEntry}
                            disabled={!report || loading || pdfExporting || deletingEntry}
                            title="Delete this persisted filter log entry"
                        >
                            {deletingEntry ? 'Deleting...' : 'Delete Entry'}
                        </button>
                    )}
                </div>
            </div>

            {loading && <div className="ddos-empty">Loading filter report...</div>}
            {error && !loading && <div className="ddos-empty">{error}</div>}

            {!loading && !error && report && (
                <div className="ddos-report-body">
                    <h5 className="ddos-report-section-title">Filter</h5>
                    <div className="ddos-report-filter-grid">
                        <div className="ddos-report-meta-item"><span>Filter Start</span><strong>{formatTime(session.started_at)}</strong></div>
                        <div className="ddos-report-meta-item"><span>Filter End</span><strong>{formatTime(session.ended_at)}</strong></div>
                        <div className="ddos-report-meta-item"><span>Duration</span><strong>{formatDurationMinSec(session.duration_seconds)}</strong></div>
                        <div className="ddos-report-meta-item"><span>Reason</span><strong>{formatFilterReason(session.reason)}</strong></div>
                    </div>

                    <div className="ddos-report-pattern-card">
                        <span className="ddos-report-pattern-label">Pattern</span>
                        <PatternDisplay pattern={session.pattern || '—'} />
                    </div>

                    <h5 className="ddos-report-section-title">Summary</h5>
                    <div className="ddos-report-summary-layout">
                        {REPORT_SUMMARY_GROUPS.map((group) => (
                            <div key={group.key} className="ddos-report-summary-group">
                                <h6 className="ddos-report-summary-group-title">{group.title}</h6>
                                <div className="ddos-report-summary-grid">
                                    {group.fields.map((fieldKey) => (
                                        <div key={fieldKey} className="ddos-report-summary-item">
                                            <span>{REPORT_SUMMARY_LABELS[fieldKey] || fieldKey}</span>
                                            <strong>{formatReportMetric(fieldKey, summary[fieldKey])}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <h5 className="ddos-report-section-title">Traffic Graphs</h5>
                    <div className="ddos-report-chart-grid">
                        {REPORT_COMBINED_CHARTS.map((chart) => (
                            <CombinedSeriesChart
                                key={chart.key}
                                title={chart.title}
                                lines={chart.lines}
                                reportSeries={reportSeries}
                            />
                        ))}
                    </div>

                    <h5 className="ddos-report-section-title">Event Timeline</h5>
                    <div className="ddos-table-wrap">
                        <table className="ddos-table">
                            <thead>
                                <tr>
                                    <th className="ddos-th">Time</th>
                                    <th className="ddos-th">Event</th>
                                    <th className="ddos-th">State</th>
                                    <th className="ddos-th">Pattern</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportEvents.length === 0 ? (
                                    <tr className="ddos-tr"><td className="ddos-td" colSpan={4}>No report events</td></tr>
                                ) : reportEvents.map((ev, i) => (
                                    <tr key={`${ev.id ?? i}-${ev.occurred_at ?? i}`} className="ddos-tr">
                                        <td className="ddos-td ddos-td-secondary">{formatTime(ev.occurred_at)}</td>
                                        <td className="ddos-td"><span className={`ddos-event-type ${ev.event_type}`}>{ev.event_type}</span></td>
                                        <td className="ddos-td"><span className={`ddos-rule-state ddos-state-${stateClass(ev.state)}`}>{ev.state}</span></td>
                                        <td className="ddos-td"><PatternDisplay pattern={ev.pattern} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Rules table component ─────────────────────────────────────────

function RulesTable({ rules, selectedRule, onSelectRule }) {
    const sortedRules = [...rules].sort((a, b) => {
        return String(a?.rule_id ?? '').localeCompare(
            String(b?.rule_id ?? ''),
            undefined,
            { numeric: true, sensitivity: 'base' },
        );
    });

    const StatStack = ({ pps, bps }) => {
        const ppsValue = Number.isFinite(Number(pps)) ? Number(pps) : null;
        const bpsValue = Number.isFinite(Number(bps)) ? Number(bps) : null;
        return (
            <span className="ddos-stat-stack">
                <span className="ddos-stat-main">{ppsValue == null ? '—' : `${formatPps(ppsValue)} pps`}</span>
                <span className="ddos-stat-sub">{formatBps(bpsValue)}</span>
            </span>
        );
    };

    return (
        <div className="ddos-table-card ddos-rules-card">
            <div className="ddos-table-wrap ddos-rules-table-wrap">
                <table className="ddos-table ddos-rules-table">
                    <colgroup>
                        <col className="ddos-col-rule" />
                        <col className="ddos-col-state" />
                        <col className="ddos-col-traffic" />
                        <col className="ddos-col-blocked" />
                        <col className="ddos-col-threshold" />
                        <col className="ddos-col-baseline" />
                        <col className="ddos-col-ratio" />
                        <col className="ddos-col-filters" />
                    </colgroup>
                    <thead>
                        <tr className="ddos-rules-head-main">
                            <th className="ddos-th ddos-rule-col"><span className="ddos-head-main-label">Rule</span></th>
                            <th className="ddos-th"><span className="ddos-head-main-label">State</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Traffic</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Blocked</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Threshold</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Baseline</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Blocked %</span></th>
                            <th className="ddos-th ddos-align-right"><span className="ddos-head-main-label">Filters</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRules.map((rule, i) => {
                            const cls = stateClass(rule.state);
                            const totalPps = Number(rule.total_pps) || 0;
                            const filteredPps = Number(rule.filtered_pps) || 0;
                            const ratio = totalPps > 0 ? (filteredPps / totalPps) * 100 : 0;
                            const ratioDisplay = Math.min(Math.max(ratio, 0), 100);
                            const isSelected = selectedRule === rule.rule_id;

                            return (
                                <tr
                                    key={rule.rule_id ?? i}
                                    className={`ddos-tr ddos-rule-row${isSelected ? ' selected' : ''}`}
                                    onClick={() => onSelectRule(rule.rule_id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onSelectRule(rule.rule_id);
                                        }
                                    }}
                                >
                                    <td className="ddos-td ddos-rule-cell ddos-rule-col" title={rule.rule_id || ''}>
                                        <span className="ddos-rule-cell-inner">
                                            <span className={`ddos-rule-dot ddos-accent-${cls}`} />
                                            <span className="ddos-rule-id">{rule.rule_id}</span>
                                        </span>
                                    </td>
                                    <td className="ddos-td"><span className={`ddos-rule-state ddos-state-${cls}`}>{rule.state || 'IDLE'}</span></td>
                                    <td className="ddos-td ddos-align-right"><StatStack pps={rule.total_pps} bps={rule.total_bps} /></td>
                                    <td className="ddos-td ddos-align-right"><StatStack pps={rule.filtered_pps} bps={rule.filtered_bps} /></td>
                                    <td className="ddos-td ddos-align-right"><StatStack pps={rule.threshold_pps} bps={rule.threshold_bps} /></td>
                                    <td className="ddos-td ddos-align-right"><StatStack pps={rule.baseline_pps} bps={rule.baseline_bps} /></td>
                                    <td className="ddos-td ddos-align-right">
                                        <div className="ddos-ratio-cell" style={{ '--ddos-ratio': `${ratioDisplay}%` }}>
                                            <span className="ddos-td-mono">{formatPercent(ratioDisplay)}</span>
                                            <span className="ddos-ratio-track">
                                                <span className="ddos-ratio-fill" />
                                            </span>
                                        </div>
                                    </td>
                                    <td className="ddos-td ddos-td-mono ddos-align-right">{rule.filter_count ?? 0}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Grafana panels component ──────────────────────────────────────

const GF_DASH_ID = 'ef176ff9-9c37-43bf-ac5f-0d1626c6927d';

const TIME_RANGES = [
    { label: '15m', value: 'now-15m' },
    { label: '1h',  value: 'now-1h'  },
    { label: '3h',  value: 'now-3h'  },
    { label: '6h',  value: 'now-6h'  },
    { label: '12h', value: 'now-12h' },
    { label: '24h', value: 'now-24h' },
    { label: '7d',  value: 'now-7d'  },
];

const FILTERS_REFRESH_MS = 3000;
const FILTER_LOG_REFRESH_MS = 10000;
const EVENTS_PER_RULE_FETCH_LIMIT = 200;
const EVENTS_MAX_PER_RULE_BUFFER = 200;
const EVENTS_MAX_TOTAL_BUFFER = 1000;
const FILTER_LOG_PER_RULE_FETCH_LIMIT = 1000;
const FILTER_LOG_GLOBAL_FETCH_LIMIT = 1000;
const FILTER_LOG_MAX_PER_RULE_BUFFER = 1000;
const FILTER_LOG_MAX_TOTAL_BUFFER = 5000;
const LOG_PAGE_SIZE = 10;
const PDF_EVENT_ROWS_SECOND_PAGE = 18;
const PDF_RENDER_WIDTH_PX = 1122;
const PDF_RENDER_SCALE = 2;
const PDF_PAGE_MARGIN_MM = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
};

function GrafanaPanels({ routerId, ruleId }) {
    const ppsActivityFrameRef = useActivityFrame();
    const bpsActivityFrameRef = useActivityFrame();
    const [timeRange, setTimeRange] = useState('now-1h');
    const { resolvedTheme } = useContext(ThemeContext);
    const [isPageVisible, setIsPageVisible] = useState(() => {
        if (typeof document === 'undefined') return true;
        return document.visibilityState !== 'hidden';
    });

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const onVisibilityChange = () => {
            setIsPageVisible(document.visibilityState !== 'hidden');
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    const gfBase = `${apiBase()}/gf`;
    const refreshParam = isPageVisible ? '&refresh=5s' : '';
    const timeParams = `from=${timeRange}&to=now&timezone=browser&theme=${resolvedTheme}${refreshParam}`;
    const varParams = `var-router=${encodeURIComponent(routerId)}&var-rule=${encodeURIComponent(ruleId)}`;

    const ppsUrl = `${gfBase}/d-solo/${GF_DASH_ID}/ddosguard?${timeParams}&${varParams}&panelId=2&hideLogo=true`;
    const bpsUrl = `${gfBase}/d-solo/${GF_DASH_ID}/ddosguard?${timeParams}&${varParams}&panelId=3&hideLogo=true`;

    return (
        <div className="ddos-grafana-section">
            <div className="ddos-section-header">
                <h2 className="ddos-section-title">
                    Graphs
                </h2>
                <div className="ddos-time-range-picker">
                    {TIME_RANGES.map(r => (
                        <button
                            key={r.value}
                            className={`ddos-time-range-btn${timeRange === r.value ? ' active' : ''}`}
                            onClick={() => setTimeRange(r.value)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="ddos-grafana-grid">
                <div className="ddos-grafana-panel">
                    <div className="ddos-grafana-panel-label">Packets per second</div>
                    {isPageVisible ? (
                        <iframe
                            ref={ppsActivityFrameRef}
                            key={ppsUrl}
                            src={ppsUrl}
                            className="ddos-grafana-iframe"
                            title={`${ruleId} — pps`}
                            frameBorder="0"
                            sandbox="allow-scripts allow-forms allow-storage-access-by-user-activation allow-same-origin"
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    ) : (
                        <div className="ddos-empty">Graph refresh paused while tab is hidden</div>
                    )}
                </div>
                <div className="ddos-grafana-panel">
                    <div className="ddos-grafana-panel-label">Bits per second</div>
                    {isPageVisible ? (
                        <iframe
                            ref={bpsActivityFrameRef}
                            key={bpsUrl}
                            src={bpsUrl}
                            className="ddos-grafana-iframe"
                            title={`${ruleId} — bps`}
                            frameBorder="0"
                            sandbox="allow-scripts allow-forms allow-storage-access-by-user-activation allow-same-origin"
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    ) : (
                        <div className="ddos-empty">Graph refresh paused while tab is hidden</div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Filters table component ────────────────────────────────────────

function FiltersTable({
    filters,
    selectedRule,
    deletingFilterKeys,
    onDeleteFilter,
    showRule = true,
    showActions = true,
    emptyMessage,
}) {
    const title = 'Active Filters';

    if (!filters.length) {
        return (
            <div className="ddos-table-card">
                <div className="ddos-table-header">
                    <h3 className="ddos-table-title">{title}</h3>
                    <span className="ddos-badge secondary">{filters.length}</span>
                </div>
                <div className="ddos-empty">
                    {emptyMessage || (selectedRule ? 'No active filters for selected rule' : 'Select a rule to view active filters')}
                </div>
            </div>
        );
    }
    return (
        <div className="ddos-table-card">
            <div className="ddos-table-header">
                <h3 className="ddos-table-title">{title}</h3>
                <span className="ddos-badge secondary">{filters.length}</span>
            </div>
            <div className="ddos-table-wrap">
                <table className="ddos-table ddos-filters-table">
                    <colgroup>
                        <col className="ddos-fcol-router" />
                        {showRule && <col className="ddos-fcol-rule" />}
                        <col className="ddos-fcol-pattern" />
                        <col className="ddos-fcol-drop-rate" />
                        <col className="ddos-fcol-dropped" />
                        <col className="ddos-fcol-peak" />
                        <col className="ddos-fcol-since" />
                        {showActions && <col className="ddos-fcol-actions" />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="ddos-th">Router</th>
                            {showRule && <th className="ddos-th">Rule</th>}
                            <th className="ddos-th">Pattern</th>
                            <th className="ddos-th ddos-align-right">Filter Rate (pps)</th>
                            <th className="ddos-th ddos-align-right">Filter Rate (bps)</th>
                            <th className="ddos-th ddos-align-right">Peak</th>
                            <th className="ddos-th ddos-align-right">Since</th>
                            {showActions && <th className="ddos-th ddos-filter-action-col">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filters.map((f) => {
                            const key = filterKeyOf(f);
                            const isQueued = deletingFilterKeys.has(key);
                            const filteredBps = Number.isFinite(Number(f.current_bps))
                                ? Number(f.current_bps)
                                : Number.isFinite(Number(f.dropped_bits))
                                    ? Number(f.dropped_bits)
                                    : Number.isFinite(Number(f.dropped_bytes))
                                        ? Number(f.dropped_bytes) * 8
                                        : null;

                            return (
                                <tr key={key} className="ddos-tr">
                                    <td className="ddos-td">{f.router_id}</td>
                                    {showRule && <td className="ddos-td">{f.rule_id}</td>}
                                    <td className="ddos-td"><PatternDisplay pattern={f.pattern} /></td>
                                    <td className="ddos-td ddos-td-mono ddos-align-right">{formatPps(f.current_pps)} pps</td>
                                    <td className="ddos-td ddos-td-mono ddos-align-right">{formatBps(filteredBps)}</td>
                                    <td className="ddos-td ddos-td-mono ddos-align-right">{formatPps(f.peak_pps)} pps</td>
                                    <td className="ddos-td ddos-td-secondary ddos-align-right">{timeAgo(f.created_at)}</td>
                                    {showActions && (
                                        <td className="ddos-td ddos-filter-action-cell ddos-filter-action-col">
                                            <button
                                                className="ddos-filter-delete-btn"
                                                onClick={() => onDeleteFilter?.(f)}
                                                disabled={isQueued || f.filter_id == null}
                                                title={isQueued ? 'Removal already queued' : 'Delete active filter'}
                                            >
                                                {isQueued ? 'Queued' : 'Delete'}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Filter log table component ──────────────────────────────────

function HistoricalFiltersTable({
    events,
    selectedRule,
    selectedRouter,
    isAdmin = false,
    deletingLogs = false,
    deletingLogEntryKeys = new Set(),
    onDeleteLogs,
    onDeleteLogEntry,
    showRouter = false,
    showRule = false,
    showReport = true,
    emptyMessage,
}) {
    const [visibleCount, setVisibleCount] = useState(LOG_PAGE_SIZE);
    const [reportData, setReportData] = useState(null);
    const [reportError, setReportError] = useState(null);
    const [reportLoadingKey, setReportLoadingKey] = useState(null);
    const [reportEntry, setReportEntry] = useState(null);
    const [pdfExporting, setPdfExporting] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const reportCardRef = useRef(null);
    const canDeleteEntries = isAdmin && typeof onDeleteLogEntry === 'function';
    const showActions = showReport || canDeleteEntries;

    useEffect(() => {
        setVisibleCount(LOG_PAGE_SIZE);
        setReportData(null);
        setReportError(null);
        setReportLoadingKey(null);
        setReportEntry(null);
        setPdfExporting(false);
        setPdfError(null);
    }, [selectedRule]);

    const buildReportKey = (f) => filterLogEntryKeyOf(f);

    const handleGenerateReport = async (f) => {
        const key = buildReportKey(f);
        setReportLoadingKey(key);
        setReportData(null);
        setReportError(null);
        setReportEntry(f);
        setPdfError(null);

        try {
            const report = await api.fetchFilterReport({
                routerId: f.router_id,
                ruleId: f.rule_id,
                filterId: f.filter_id,
                startedAt: f.added_at || undefined,
                stepSeconds: 1,
                includeSeries: true,
            });
            setReportData(report);
        } catch (err) {
            setReportError(err?.message || 'Failed to load filter report');
        } finally {
            setReportLoadingKey(null);
        }
    };

    // Build historical filter list from filter_add / filter_remove event pairs
    const historicalFilters = (() => {
        // Key on router+rule+filter_id — filter_ids are reused across rules
        const key = ev => `${ev.router_id}|${ev.rule_id}|${ev.filter_id}`;
        const addMap = {};
        const rows = [];

        // Events are newest-first; iterate in chronological order
        const chronological = [...events].reverse();
        for (const ev of chronological) {
            if (ev.event_type === 'filter_add' && ev.filter_id != null) {
                addMap[key(ev)] = ev;
            } else if (ev.event_type === 'filter_remove' && ev.filter_id != null) {
                const add = addMap[key(ev)];
                rows.push({
                    filter_id: ev.filter_id,
                    pattern: ev.pattern || add?.pattern || '—',
                    router_id: ev.router_id,
                    rule_id: ev.rule_id,
                    added_at: add?.occurred_at || null,
                    removed_at: ev.occurred_at,
                    add_event_id: add?.id ?? null,
                    remove_event_id: ev.id ?? null,
                    duration: add?.occurred_at
                        ? Math.round((new Date(ev.occurred_at) - new Date(add.occurred_at)) / 1000)
                        : null,
                });
                delete addMap[key(ev)];
            }
        }

        // Endpoint may contain ended filter_add rows even when filter_remove is missing.
        // Keep these visible instead of dropping them silently.
        Object.values(addMap).forEach((add) => {
            rows.push({
                filter_id: add.filter_id,
                pattern: add.pattern || '—',
                router_id: add.router_id,
                rule_id: add.rule_id,
                added_at: add.occurred_at || null,
                removed_at: null,
                add_event_id: add.id ?? null,
                remove_event_id: null,
                duration: null,
            });
        });

        const timestampOf = (row) => {
            const t = Date.parse(row?.removed_at || row?.added_at || '');
            return Number.isFinite(t) ? t : 0;
        };

        return rows.sort((a, b) => timestampOf(b) - timestampOf(a));
    })();

    const visibleRows = historicalFilters.slice(0, visibleCount);
    const canShowMore = visibleCount < historicalFilters.length;
    const canShowLess = visibleCount > LOG_PAGE_SIZE;
    const reportOpen = reportLoadingKey != null || reportError != null || reportData != null;

    const closeReport = () => {
        setReportData(null);
        setReportError(null);
        setReportLoadingKey(null);
        setReportEntry(null);
        setPdfExporting(false);
        setPdfError(null);
    };

    const reportEntryKey = reportEntry ? filterLogEntryKeyOf(reportEntry) : null;
    const reportEntryHasPersistedEvents = reportEntry ? filterLogEntryEventIds(reportEntry).length > 0 : false;
    const deletingReportEntry = reportEntryKey ? deletingLogEntryKeys.has(reportEntryKey) : false;

    const handleDeleteReportEntry = async () => {
        if (!reportEntry || !onDeleteLogEntry) return;
        const deleted = await onDeleteLogEntry(reportEntry);
        if (deleted) closeReport();
    };

    const reportBaseName = (() => {
        if (!reportData) return 'ddos-filter-report';
        return `ddos-filter-report-${reportData.router_id}-${reportData.rule_id}-${reportData.filter_id}`
            .replace(/[^a-zA-Z0-9._-]+/g, '_');
    })();

    const handleExportJson = () => {
        if (!reportData) return;
        downloadTextFile(
            `${reportBaseName}.json`,
            `${JSON.stringify(reportData, null, 2)}\n`,
            'application/json;charset=utf-8',
        );
    };

    const handleExportCsv = () => {
        if (!reportData) return;
        downloadTextFile(
            `${reportBaseName}.csv`,
            buildFilterReportCsv(reportData),
            'text/csv;charset=utf-8',
        );
    };

    const handleExportPdf = async () => {
        if (!reportData || pdfExporting) return;

        const sourceCard = reportCardRef.current;
        if (!sourceCard) {
            setPdfError('Report view is not ready for export yet. Please try again.');
            return;
        }

        const sourceRows = Array.from(sourceCard.querySelectorAll('.ddos-table-wrap tbody > tr'));
        const chartImages = collectPdfChartImages(sourceCard);
        const hasEvents = Array.isArray(reportData.events) && reportData.events.length > 0;
        const eventRows = hasEvents ? sourceRows.slice(0, PDF_EVENT_ROWS_SECOND_PAGE) : [];
        const hiddenEventsCount = Math.max(0, sourceRows.length - eventRows.length);
        const pageDescriptors = [
            { type: 'overview', chartImages },
            {
                type: 'events',
                rows: eventRows,
                title: 'Event Timeline',
                shownCount: eventRows.length,
                totalCount: sourceRows.length,
                moreCount: hiddenEventsCount,
            },
        ];

        const exportedAtText = formatTime(new Date().toISOString());
        const mount = document.createElement('div');
        mount.className = 'ddos-pdf-render-host';
        document.body.appendChild(mount);

        setPdfExporting(true);
        setPdfError(null);

        try {
            const [{ jsPDF }, html2canvasModule] = await Promise.all([
                import('jspdf'),
                import('html2canvas'),
            ]);
            const html2canvas = html2canvasModule.default;

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });

            const pageWidthMm = pdf.internal.pageSize.getWidth();
            const pageHeightMm = pdf.internal.pageSize.getHeight();
            const usableWidthMm = pageWidthMm - PDF_PAGE_MARGIN_MM.left - PDF_PAGE_MARGIN_MM.right;
            const usableHeightMm = pageHeightMm - PDF_PAGE_MARGIN_MM.top - PDF_PAGE_MARGIN_MM.bottom;

            for (let i = 0; i < pageDescriptors.length; i += 1) {
                if (i > 0) pdf.addPage('a4', 'landscape');

                const pageNode = buildPdfReportFrame({
                    sourceCard,
                    descriptor: pageDescriptors[i],
                    report: reportData,
                    exportedAtText,
                    pageIndex: i,
                    totalPages: pageDescriptors.length,
                });

                mount.replaceChildren(pageNode);
                await waitForDomPaint();
                await waitForFontsLoaded(pageNode);
                await waitForImagesLoaded(pageNode);

                const canvas = await html2canvas(pageNode, {
                    backgroundColor: '#ffffff',
                    scale: PDF_RENDER_SCALE,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    width: PDF_RENDER_WIDTH_PX,
                    windowWidth: PDF_RENDER_WIDTH_PX,
                });

                const imageWidthMm = usableWidthMm;
                const imageHeightMm = (canvas.height * imageWidthMm) / canvas.width;
                const boundedHeightMm = Math.min(imageHeightMm, usableHeightMm);
                const boundedWidthMm = imageWidthMm * (boundedHeightMm / imageHeightMm);
                const offsetX = (pageWidthMm - boundedWidthMm) / 2;

                pdf.addImage(
                    canvas.toDataURL('image/png'),
                    'PNG',
                    offsetX,
                    PDF_PAGE_MARGIN_MM.top,
                    boundedWidthMm,
                    boundedHeightMm,
                    undefined,
                    'FAST',
                );
            }

            pdf.save(`${reportBaseName}.pdf`);
        } catch (err) {
            setPdfError(err?.message || 'Failed to generate PDF export.');
        } finally {
            mount.remove();
            setPdfExporting(false);
        }
    };

    return (
        <>
            <div className="ddos-table-card">
                <div className="ddos-table-header">
                    <h3 className="ddos-table-title">Filter Log</h3>
                    <div className="ddos-table-header-actions">
                        <span className="ddos-badge secondary">{historicalFilters.length}</span>
                        {isAdmin && onDeleteLogs && (
                            <button
                                className="ddos-log-btn danger"
                                onClick={() => onDeleteLogs?.()}
                                disabled={deletingLogs || !selectedRouter}
                                title="Delete persisted filter log rows for the selected scope"
                            >
                                {deletingLogs ? 'Deleting...' : 'Delete Filter Logs'}
                            </button>
                        )}
                    </div>
                </div>
                {historicalFilters.length === 0 ? (
                    <div className="ddos-empty">{emptyMessage || 'No filter log entries for selected rule'}</div>
                ) : (
                    <>
                        <div className="ddos-table-wrap">
                            <table className="ddos-table">
                                <thead>
                                    <tr>
                                        {showRouter && <th className="ddos-th">Router</th>}
                                        {showRule && <th className="ddos-th">Rule</th>}
                                        <th className="ddos-th">Pattern</th>
                                        <th className="ddos-th">Added</th>
                                        <th className="ddos-th">Removed</th>
                                        <th className="ddos-th">Duration</th>
                                        {showActions && <th className="ddos-th ddos-align-right ddos-filter-log-action-col">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map((f, i) => {
                                        const rowKey = buildReportKey(f);
                                        const isLoadingRow = reportLoadingKey === rowKey;
                                        const isDeletingRow = deletingLogEntryKeys.has(rowKey);
                                        const hasPersistedEvents = filterLogEntryEventIds(f).length > 0;

                                        return (
                                            <tr key={`${rowKey}-${i}`} className="ddos-tr">
                                                {showRouter && <td className="ddos-td">{f.router_id}</td>}
                                                {showRule && <td className="ddos-td">{f.rule_id}</td>}
                                                <td className="ddos-td"><PatternDisplay pattern={f.pattern} /></td>
                                                <td className="ddos-td ddos-td-secondary">{formatTime(f.added_at)}</td>
                                                <td className="ddos-td ddos-td-secondary">{formatTime(f.removed_at)}</td>
                                                <td className="ddos-td ddos-td-mono">
                                                    {f.duration != null
                                                        ? f.duration >= 3600
                                                            ? `${Math.floor(f.duration / 3600)}h ${Math.floor((f.duration % 3600) / 60)}m`
                                                            : f.duration >= 60
                                                                ? `${Math.floor(f.duration / 60)}m ${f.duration % 60}s`
                                                                : `${f.duration}s`
                                                        : '—'}
                                                </td>
                                                {showActions && (
                                                    <td className="ddos-td ddos-align-right ddos-filter-log-action-cell">
                                                        <div className="ddos-filter-log-actions">
                                                            {showReport && (
                                                                <button
                                                                    className="ddos-log-btn ddos-filter-report-btn"
                                                                    onClick={() => handleGenerateReport(f)}
                                                                    disabled={reportLoadingKey != null || isDeletingRow}
                                                                >
                                                                    {isLoadingRow ? 'Loading...' : 'Report'}
                                                                </button>
                                                            )}
                                                            {canDeleteEntries && (
                                                                <button
                                                                    className="ddos-log-btn danger ddos-filter-log-delete-btn"
                                                                    onClick={() => onDeleteLogEntry?.(f)}
                                                                    disabled={deletingLogs || isDeletingRow || !hasPersistedEvents}
                                                                    title={hasPersistedEvents
                                                                        ? 'Delete this persisted filter log entry'
                                                                        : 'Waiting for this log entry to be persisted'}
                                                                >
                                                                    {isDeletingRow ? 'Deleting...' : 'Delete'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {historicalFilters.length > LOG_PAGE_SIZE && (
                            <div className="ddos-log-controls">
                                <span className="ddos-log-count">
                                    Showing {Math.min(visibleCount, historicalFilters.length)} of {historicalFilters.length}
                                </span>
                                <div className="ddos-log-actions">
                                    {canShowLess && (
                                        <button
                                            className="ddos-log-btn secondary"
                                            onClick={() => setVisibleCount(LOG_PAGE_SIZE)}
                                        >
                                            Show less
                                        </button>
                                    )}
                                    <button
                                        className="ddos-log-btn"
                                        onClick={() => setVisibleCount(v => v + LOG_PAGE_SIZE)}
                                        disabled={!canShowMore}
                                    >
                                        Show 10 more
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Modal
                show={reportOpen}
                onClose={closeReport}
                title="Filter Report"
                maxWidth="xl"
                hideActions
                showCloseIcon
            >
                {pdfError && (
                    <div className="ddos-error-banner ddos-report-export-error-banner">
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>error</span>
                        <span>{pdfError}</span>
                        <button
                            className="ddos-report-export-error-dismiss"
                            onClick={() => setPdfError(null)}
                            aria-label="Dismiss PDF export error"
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>close</span>
                        </button>
                    </div>
                )}
                <FilterReportPanel
                    report={reportData}
                    loading={reportLoadingKey != null}
                    error={reportError}
                    onExportJson={handleExportJson}
                    onExportCsv={handleExportCsv}
                    onExportPdf={handleExportPdf}
                    onDeleteEntry={canDeleteEntries && reportEntryHasPersistedEvents ? handleDeleteReportEntry : undefined}
                    pdfExporting={pdfExporting}
                    deletingEntry={deletingReportEntry}
                    inModal
                    rootRef={reportCardRef}
                />
            </Modal>
        </>
    );
}

// ── Events table component ─────────────────────────────────────────

function EventsTable({
    events,
    selectedRule,
    selectedRouter,
    isAdmin = false,
    deletingLogs = false,
    onDeleteLogs,
}) {
    const [visibleCount, setVisibleCount] = useState(LOG_PAGE_SIZE);

    useEffect(() => {
        setVisibleCount(LOG_PAGE_SIZE);
    }, [selectedRule]);

    const visibleRows = events.slice(0, visibleCount);
    const canShowMore = visibleCount < events.length;
    const canShowLess = visibleCount > LOG_PAGE_SIZE;

    return (
        <div className="ddos-table-card">
            <div className="ddos-table-header">
                <h3 className="ddos-table-title">Event Log</h3>
                <div className="ddos-table-header-actions">
                    <span className="ddos-badge secondary">{events.length}</span>
                    {isAdmin && (
                        <button
                            className="ddos-log-btn danger"
                            onClick={() => onDeleteLogs?.()}
                            disabled={deletingLogs || !selectedRouter}
                            title="Delete persisted event log rows for the selected scope"
                        >
                            {deletingLogs ? 'Deleting...' : 'Delete Event Logs'}
                        </button>
                    )}
                </div>
            </div>
            {events.length === 0 ? (
                <div className="ddos-empty">No events for selected rule</div>
            ) : (
                <>
                    <div className="ddos-table-wrap">
                        <table className="ddos-table">
                            <thead>
                                <tr>
                                    <th className="ddos-th">Time</th>
                                    <th className="ddos-th">Router</th>
                                    <th className="ddos-th">Rule</th>
                                    <th className="ddos-th">Event</th>
                                    <th className="ddos-th">State</th>
                                    <th className="ddos-th">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((ev, i) => (
                                    <tr key={eventUiKeyOf(ev, i)} className="ddos-tr">
                                        <td className="ddos-td ddos-td-secondary">{formatTime(ev.occurred_at)}</td>
                                        <td className="ddos-td">{ev.router_id}</td>
                                        <td className="ddos-td">{ev.rule_id}</td>
                                        <td className="ddos-td">
                                            <span className={`ddos-event-type ${ev.event_type}`}>{ev.event_type}</span>
                                        </td>
                                        <td className="ddos-td">
                                            <span className={`ddos-rule-state ddos-state-${stateClass(ev.state)}`}>{ev.state}</span>
                                        </td>
                                        <td className="ddos-td"><PatternDisplay pattern={ev.pattern} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {events.length > LOG_PAGE_SIZE && (
                        <div className="ddos-log-controls">
                            <span className="ddos-log-count">
                                Showing {Math.min(visibleCount, events.length)} of {events.length}
                            </span>
                            <div className="ddos-log-actions">
                                {canShowLess && (
                                    <button
                                        className="ddos-log-btn secondary"
                                        onClick={() => setVisibleCount(LOG_PAGE_SIZE)}
                                    >
                                        Show less
                                    </button>
                                )}
                                <button
                                    className="ddos-log-btn"
                                    onClick={() => setVisibleCount(v => v + LOG_PAGE_SIZE)}
                                    disabled={!canShowMore}
                                >
                                    Show 10 more
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ── Main DdosGuard page ────────────────────────────────────────────

function DdosGuard() {
    const { selectedOrganization, authUser, userRole } = useContext(AuthContext);

    // Resolve the numeric organization_id from the authUser's org list.
    // selectedOrganization may be a numeric id or an org name (Nav stores org.id ?? org.name).
    const activeOrgId = (() => {
        if (!selectedOrganization) return null;
        const orgs = authUser?.organizations || [];
        const match = orgs.find(o => String(o.id ?? o.name) === String(selectedOrganization));
        // Prefer the numeric id; fall back to selectedOrganization itself if it looks numeric
        const id = match?.id ?? (Number.isInteger(Number(selectedOrganization)) ? Number(selectedOrganization) : null);
        return id || null;
    })();
    const isAdmin = useMemo(() => {
        const values = [userRole, authUser?.roles, authUser?.role];
        return values.some((value) => {
            if (Array.isArray(value)) {
                return value.some(item => String(item || '').trim().toLowerCase() === 'admin');
            }

            return String(value || '')
                .split(/[\s,]+/)
                .map(item => item.trim().toLowerCase())
                .includes('admin');
        });
    }, [userRole, authUser]);
    const [routers, setRouters] = useState([]);
    const [selectedRouter, setSelectedRouter] = useState(null);
    const [rules, setRules] = useState([]);
    const [selectedRule, setSelectedRule] = useState(null);
    const [grafanaReady, setGrafanaReady] = useState(false);
    const [filters, setFilters] = useState([]);
    const [deletingFilterKeys, setDeletingFilterKeys] = useState(() => new Set());
    const [deletingFilterLogs, setDeletingFilterLogs] = useState(false);
    const [deletingFilterLogEntryKeys, setDeletingFilterLogEntryKeys] = useState(() => new Set());
    const [deletingEventLogs, setDeletingEventLogs] = useState(false);
    const [events, setEvents] = useState([]);
    const [filterLogEvents, setFilterLogEvents] = useState([]);
    const [viewMode, setViewMode] = useState('basic');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sseConnected, setSseConnected] = useState(false);
    const eventSourceRef = useRef(null);
    const liveEventSeqRef = useRef(0);
    const filtersRef = useRef([]);
    const filterTombstonesRef = useRef(new Map());

    const commitFilterState = useCallback(({ filters: nextFilters, tombstones }) => {
        const normalizedFilters = Array.isArray(nextFilters) ? nextFilters : [];
        filtersRef.current = normalizedFilters;
        if (tombstones instanceof Map) {
            filterTombstonesRef.current = tombstones;
        }
        setFilters(normalizedFilters);
    }, []);

    const resetFilterState = useCallback(() => {
        filtersRef.current = [];
        filterTombstonesRef.current = new Map();
        setFilters([]);
    }, []);

    // Drop stale queued-delete keys when filters disappear (e.g. SSE filter_remove)
    useEffect(() => {
        setDeletingFilterKeys(prev => {
            if (prev.size === 0) return prev;
            const activeKeys = new Set(filters.filter(f => f.filter_id != null).map(filterKeyOf));
            const next = new Set([...prev].filter(k => activeKeys.has(k)));
            return next.size === prev.size ? prev : next;
        });
    }, [filters]);

    // ── Grafana session bootstrap ─────────────────────────────
    useEffect(() => {
        setGrafanaReady(Boolean(authUser));
    }, [activeOrgId, authUser]);

    // ── Initial data load ──────────────────────────────────────
    const loadRouters = useCallback(async () => {
        try {
            const data = await api.fetchRouters(activeOrgId);
            const nextRouters = (data.routers || []).map(router => ({
                ...router,
                telemetry_status: normalizeTelemetryStatus(router?.telemetry_status),
            }));
            setRouters(nextRouters);
            return nextRouters;
        } catch (err) {
            setError('Failed to load routers');
            return [];
        }
    }, [activeOrgId]);

    const loadRules = useCallback(async (routerId) => {
        try {
            const data = await api.fetchRouterRules(routerId);
            const nextRules = data.rules || [];
            setRules(nextRules);

            setRouters(prev => prev.map((router) => {
                if (router.id !== routerId) return router;

                return {
                    ...router,
                    telemetry_status: normalizeTelemetryStatus(data?.telemetry_status ?? router.telemetry_status),
                    telemetry_last_seen: data?.telemetry_last_seen ?? router.telemetry_last_seen ?? null,
                };
            }));

            return nextRules;
        } catch {
            setRules([]);
            return [];
        }
    }, []);

    const loadFilters = useCallback(async (routerId, { silent = false } = {}) => {
        try {
            const data = await api.fetchFilters(routerId, activeOrgId);
            const now = Date.now();
            const result = reconcileRestFilters(filtersRef.current, data.filters || [], {
                routerId,
                now,
                tombstones: filterTombstonesRef.current,
            });

            commitFilterState(result);
            return result.filters;
        } catch {
            if (!silent) {
                setError('Failed to load active filters');
            }
            return null;
        }
    }, [activeOrgId, commitFilterState]);

    const loadEvents = useCallback(async (routerId, ruleIds = []) => {
        try {
            const uniqueRuleIds = [...new Set(
                (Array.isArray(ruleIds) ? ruleIds : [])
                    .map(ruleId => String(ruleId ?? '').trim())
                    .filter(Boolean),
            )];

            let mergedEvents = [];

            if (routerId && uniqueRuleIds.length > 0) {
                // Query each rule separately so a noisy rule cannot starve others due API row limits.
                const perRuleResults = await Promise.allSettled(
                    uniqueRuleIds.map(ruleId => api.fetchEvents({
                        organizationId: activeOrgId,
                        routerId,
                        ruleId,
                        limit: EVENTS_PER_RULE_FETCH_LIMIT,
                    })),
                );

                mergedEvents = perRuleResults.flatMap((result) => (
                    result.status === 'fulfilled' && Array.isArray(result.value?.events)
                        ? result.value.events
                        : []
                ));
            } else {
                const data = await api.fetchEvents({
                    organizationId: activeOrgId,
                    routerId,
                    limit: EVENTS_MAX_TOTAL_BUFFER,
                });
                mergedEvents = Array.isArray(data?.events) ? data.events : [];
            }

            const evs = normalizeEventRows(mergedEvents, {
                maxTotal: EVENTS_MAX_TOTAL_BUFFER,
                maxPerRule: EVENTS_MAX_PER_RULE_BUFFER,
            });
            setEvents(evs);
            return evs;
        } catch {
            setEvents([]);
            return [];
        }
    }, [activeOrgId]);

    const loadFilterLogEvents = useCallback(async (routerId, ruleIds = []) => {
        try {
            const uniqueRuleIds = [...new Set(
                (Array.isArray(ruleIds) ? ruleIds : [])
                    .map(ruleId => String(ruleId ?? '').trim())
                    .filter(Boolean),
            )];

            let mergedEvents = [];

            if (routerId && uniqueRuleIds.length > 0) {
                const perRuleResults = await Promise.allSettled(
                    uniqueRuleIds.map(ruleId => api.fetchEventFilters({
                        organizationId: activeOrgId,
                        routerId,
                        ruleId,
                        limit: FILTER_LOG_PER_RULE_FETCH_LIMIT,
                    })),
                );

                mergedEvents = perRuleResults.flatMap((result) => (
                    result.status === 'fulfilled' && Array.isArray(result.value?.events)
                        ? result.value.events
                        : []
                ));
            } else {
                const data = await api.fetchEventFilters({
                    organizationId: activeOrgId,
                    routerId,
                    limit: FILTER_LOG_GLOBAL_FETCH_LIMIT,
                });
                mergedEvents = Array.isArray(data?.events) ? data.events : [];
            }

            const evs = normalizeEventRows(mergedEvents, {
                maxTotal: FILTER_LOG_MAX_TOTAL_BUFFER,
                maxPerRule: FILTER_LOG_MAX_PER_RULE_BUFFER,
            });
            setFilterLogEvents(evs);
            return evs;
        } catch {
            setFilterLogEvents([]);
            return [];
        }
    }, [activeOrgId]);

    // Keep active filter metrics fresh (current/peak/dropped) even when SSE only signals add/remove.
    useEffect(() => {
        const routerId = viewMode === 'basic' ? undefined : selectedRouter;
        if (viewMode === 'detailed' && !routerId) return undefined;

        const id = setInterval(() => {
            loadFilters(routerId, { silent: true });
        }, FILTERS_REFRESH_MS);

        return () => clearInterval(id);
    }, [viewMode, selectedRouter, loadFilters]);

    // Periodically refresh filter log history for the selected rule to recover from missed SSE remove events.
    useEffect(() => {
        if (viewMode === 'detailed' && (!selectedRouter || !selectedRule)) return undefined;

        const routerId = viewMode === 'basic' ? undefined : selectedRouter;
        const ruleIds = viewMode === 'basic' ? [] : [selectedRule];

        loadFilterLogEvents(routerId, ruleIds);

        const id = setInterval(() => {
            loadFilterLogEvents(routerId, ruleIds);
        }, FILTER_LOG_REFRESH_MS);

        return () => clearInterval(id);
    }, [viewMode, selectedRouter, selectedRule, loadFilterLogEvents]);

    const handleDeleteFilter = useCallback(async (filter) => {
        if (!filter || filter.filter_id == null) return;

        const key = filterKeyOf(filter);
        if (deletingFilterKeys.has(key)) return;

        const confirmed = window.confirm(
            `Delete active filter #${filter.filter_id} for rule "${filter.rule_id}" on router "${filter.router_id}"?`,
        );
        if (!confirmed) return;

        setDeletingFilterKeys(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });

        try {
            await api.deleteFilter(filter.router_id, filter.rule_id, filter.filter_id);

            // Keep button disabled briefly while waiting for SSE filter_remove.
            setTimeout(() => {
                setDeletingFilterKeys(prev => {
                    if (!prev.has(key)) return prev;
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
            }, 12000);
        } catch (err) {
            setError(err?.message || 'Failed to delete active filter');
            setDeletingFilterKeys(prev => {
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    }, [deletingFilterKeys]);

    const handleDeleteFilterLogs = useCallback(async () => {
        if (!isAdmin || !selectedRouter) return;

        const scope = selectedRule
            ? `router "${selectedRouter}" and rule "${selectedRule}"`
            : `router "${selectedRouter}"`;
        const confirmed = window.confirm(
            `Delete persisted filter logs for ${scope}? This action cannot be undone.`,
        );
        if (!confirmed) return;

        setDeletingFilterLogs(true);
        setError(null);

        try {
            const ruleIds = rules
                .map(rule => rule?.rule_id)
                .filter(ruleId => ruleId != null && String(ruleId).trim() !== '');

            await api.deleteLogs({
                target: 'filter_logs',
                routerId: selectedRouter,
                ruleId: selectedRule || undefined,
            });

            await Promise.all([
                loadEvents(selectedRouter, ruleIds),
                loadFilterLogEvents(selectedRouter, ruleIds),
            ]);
        } catch (err) {
            setError(err?.message || 'Failed to delete filter logs');
        } finally {
            setDeletingFilterLogs(false);
        }
    }, [isAdmin, selectedRouter, selectedRule, loadEvents, loadFilterLogEvents, rules]);

    const handleDeleteFilterLogEntry = useCallback(async (entry) => {
        if (!isAdmin || !entry) return false;

        const eventIds = filterLogEntryEventIds(entry);

        if (eventIds.length === 0) {
            setError('Cannot delete this filter log entry because its event IDs are missing.');
            return false;
        }

        const key = filterLogEntryKeyOf(entry);
        if (deletingFilterLogEntryKeys.has(key)) return false;

        const confirmed = window.confirm(
            `Delete filter log entry for filter #${entry.filter_id} on router "${entry.router_id}", rule "${entry.rule_id}"? This action cannot be undone.`,
        );
        if (!confirmed) return false;

        setDeletingFilterLogEntryKeys(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        setError(null);

        try {
            await api.deleteLogs({
                target: 'filter_logs',
                routerId: entry.router_id,
                ruleId: entry.rule_id,
                filterId: entry.filter_id,
                eventIds,
            });

            const deletedIDs = new Set(eventIds.map(id => String(id)));
            setFilterLogEvents(prev => prev.filter(ev => !deletedIDs.has(String(ev.id))));
            setEvents(prev => prev.filter(ev => !deletedIDs.has(String(ev.id))));

            const refreshRouterId = viewMode === 'basic' ? undefined : (selectedRouter || entry.router_id);
            const refreshRuleIds = viewMode === 'basic'
                ? []
                : selectedRule
                    ? [selectedRule]
                    : rules
                        .map(rule => rule?.rule_id)
                        .filter(ruleId => ruleId != null && String(ruleId).trim() !== '');

            await Promise.all([
                loadFilterLogEvents(refreshRouterId, refreshRuleIds),
                viewMode === 'basic'
                    ? Promise.resolve([])
                    : loadEvents(refreshRouterId, refreshRuleIds),
            ]);

            return true;
        } catch (err) {
            setError(err?.message || 'Failed to delete filter log entry');
            return false;
        } finally {
            setDeletingFilterLogEntryKeys(prev => {
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    }, [
        isAdmin,
        deletingFilterLogEntryKeys,
        viewMode,
        selectedRouter,
        selectedRule,
        rules,
        loadEvents,
        loadFilterLogEvents,
    ]);

    const handleDeleteEventLogs = useCallback(async () => {
        if (!isAdmin || !selectedRouter) return;

        const scope = selectedRule
            ? `router "${selectedRouter}" and rule "${selectedRule}"`
            : `router "${selectedRouter}"`;
        const confirmed = window.confirm(
            `Delete persisted event logs for ${scope}? This action cannot be undone.`,
        );
        if (!confirmed) return;

        setDeletingEventLogs(true);
        setError(null);

        try {
            await api.deleteLogs({
                target: 'events',
                routerId: selectedRouter,
                ruleId: selectedRule || undefined,
            });
            const ruleIds = rules
                .map(rule => rule?.rule_id)
                .filter(ruleId => ruleId != null && String(ruleId).trim() !== '');
            await Promise.all([
                loadEvents(selectedRouter, ruleIds),
                loadFilterLogEvents(selectedRouter, ruleIds),
            ]);
        } catch (err) {
            setError(err?.message || 'Failed to delete event logs');
        } finally {
            setDeletingEventLogs(false);
        }
    }, [isAdmin, selectedRouter, selectedRule, loadEvents, loadFilterLogEvents, rules]);

    // ── Select a router and load its data ──────────────────────
    const selectRouter = useCallback(async (routerId) => {
        setSelectedRouter(routerId);
        setSelectedRule(null);
        if (routerId) {
            const fetchedRules = await loadRules(routerId);
            const ruleIds = (fetchedRules || [])
                .map(rule => rule?.rule_id)
                .filter(ruleId => ruleId != null && String(ruleId).trim() !== '');

            await Promise.all([
                loadFilters(routerId),
                loadEvents(routerId, ruleIds),
                loadFilterLogEvents(routerId, ruleIds),
            ]);

            if (fetchedRules.length > 0) {
                setSelectedRule(fetchedRules[0].rule_id ?? null);
            }
        } else {
            setRules([]);
            await Promise.all([
                loadFilters(),
                loadEvents(),
                loadFilterLogEvents(),
            ]);
        }
    }, [loadRules, loadFilters, loadEvents, loadFilterLogEvents]);

    const handleViewModeChange = useCallback(async (nextMode) => {
        if (nextMode === viewMode) return;

        setViewMode(nextMode);
        setError(null);

        if (nextMode === 'basic') {
            await Promise.all([
                loadFilters(),
                loadFilterLogEvents(),
            ]);
            return;
        }

        if (selectedRouter) {
            await selectRouter(selectedRouter);
        } else if (routers.length > 0) {
            await selectRouter(routers[0].id);
        }
    }, [viewMode, selectedRouter, routers, loadFilters, loadFilterLogEvents, selectRouter]);

    // ── Bootstrap & re-run when active org changes ──────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setSelectedRouter(null);
            setSelectedRule(null);
            setRules([]);
            resetFilterState();
            setEvents([]);
            setFilterLogEvents([]);
            const routerList = await loadRouters();
            if (cancelled) return;

            if (viewMode === 'basic') {
                await Promise.all([loadFilters(), loadFilterLogEvents()]);
            } else if (routerList.length > 0) {
                // selectRouter also loads filters, events and filter log history.
                await selectRouter(routerList[0].id);
            } else {
                await Promise.all([loadFilters(), loadEvents(), loadFilterLogEvents()]);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrgId, resetFilterState]);

    // ── SSE live stream ────────────────────────────────────────
    useEffect(() => {
        const es = new EventSource(api.streamUrl(activeOrgId), { withCredentials: true });
        eventSourceRef.current = es;

        es.onopen = () => setSseConnected(true);

        es.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data);
                switch (msg.type) {
                    case 'connected':
                        setSseConnected(true);
                        break;

                    case 'status':
                        // Update router last_seen
                        setRouters(prev =>
                            prev.map(r =>
                                r.id === msg.router_id
                                    ? {
                                        ...r,
                                        last_seen: msg.timestamp,
                                        connected: true,
                                        live_status: 'online',
                                        telemetry_status: 'online',
                                        telemetry_last_seen: msg.timestamp,
                                    }
                                    : r
                            )
                        );
                        // Update rules if this is the selected router
                        setSelectedRouter(currentRouter => {
                            if (currentRouter === msg.router_id && msg.payload?.rules) {
                                setRules(prev => {
                                    const prevMap = Object.fromEntries(prev.map(r => [r.rule_id, r]));
                                    const next = msg.payload.rules.map(r => {
                                        // Daemon may send `id` instead of `rule_id` — normalise
                                        const id = r.rule_id ?? r.id;
                                        return { ...prevMap[id], ...r, rule_id: id };
                                    });
                                    return next;
                                });
                            }
                            return currentRouter;
                        });
                        if (Array.isArray(msg.payload?.rules)) {
                            const result = reconcileStatusFilters(filtersRef.current, msg.payload.rules, {
                                routerId: msg.router_id,
                                timestamp: msg.timestamp,
                                now: Date.now(),
                                tombstones: filterTombstonesRef.current,
                            });
                            commitFilterState(result);
                        }
                        break;

                    case 'event': {
                        const seq = ++liveEventSeqRef.current;
                        // Prepend to event log
                        const newEvent = {
                            _uiKey: `live-${msg.router_id}-${msg.payload.rule_id}-${msg.payload.event_type}-${msg.payload.filter?.id ?? 'none'}-${msg.timestamp}-${seq}`,
                            router_id: msg.router_id,
                            rule_id: msg.payload.rule_id,
                            event_type: msg.payload.event_type,
                            state: msg.payload.state,
                            filter_id: msg.payload.filter?.id ?? null,
                            pattern: msg.payload.filter?.pattern || null,
                            occurred_at: msg.timestamp,
                        };
                        setEvents(prev => normalizeEventRows([newEvent, ...prev], {
                            maxTotal: EVENTS_MAX_TOTAL_BUFFER,
                            maxPerRule: EVENTS_MAX_PER_RULE_BUFFER,
                        }));

                        if (msg.payload.event_type === 'filter_remove') {
                            setFilterLogEvents(prev => normalizeEventRows([newEvent, ...prev], {
                                maxTotal: FILTER_LOG_MAX_TOTAL_BUFFER,
                                maxPerRule: FILTER_LOG_MAX_PER_RULE_BUFFER,
                            }));
                        }

                        if (msg.payload.event_type === 'filter_add' && msg.payload.filter?.id != null) {
                            const result = applyFilterAddEvent(filtersRef.current, {
                                routerId: msg.router_id,
                                ruleId: msg.payload.rule_id,
                                filter: msg.payload.filter,
                                timestamp: msg.timestamp,
                            }, {
                                now: Date.now(),
                                tombstones: filterTombstonesRef.current,
                            });
                            commitFilterState(result);
                        } else if (msg.payload.event_type === 'filter_remove' && msg.payload.filter?.id != null) {
                            const result = applyFilterRemoveEvent(filtersRef.current, {
                                routerId: msg.router_id,
                                ruleId: msg.payload.rule_id,
                                filterId: msg.payload.filter.id,
                                timestamp: msg.timestamp,
                            }, {
                                now: Date.now(),
                                tombstones: filterTombstonesRef.current,
                            });
                            commitFilterState(result);
                        }
                        break;
                    }

                    case 'telemetry': {
                        const telemetryStatus = normalizeTelemetryStatus(msg.payload?.status);

                        setRouters(prev => prev.map((r) => {
                            if (r.id !== msg.router_id) return r;
                            return {
                                ...r,
                                telemetry_status: telemetryStatus,
                                telemetry_last_seen: msg.payload?.last_seen ?? r.telemetry_last_seen ?? null,
                            };
                        }));
                        break;
                    }

                    default:
                        break;
                }
            } catch { /* ignore malformed SSE */ }
        };

        es.onerror = () => {
            setSseConnected(false);
        };

        return () => {
            es.close();
            eventSourceRef.current = null;
        };
    }, [activeOrgId, commitFilterState]);


    // ── Render ─────────────────────────────────────────────────
    const visibleFilters = useMemo(() => {
        if (viewMode === 'basic') {
            return [...filters].sort(compareActiveFiltersStable);
        }

        return selectedRouter && selectedRule
            ? filters.filter(f => (
                String(f.router_id) === String(selectedRouter)
                && String(f.rule_id) === String(selectedRule)
            ))
            : [];
    }, [viewMode, filters, selectedRouter, selectedRule]);
    const selectedRuleEvents = useMemo(
        () => (
            selectedRouter && selectedRule
                ? events.filter(e => (
                    String(e.router_id) === String(selectedRouter)
                    && String(e.rule_id) === String(selectedRule)
                ))
                : events
        ),
        [events, selectedRouter, selectedRule],
    );
    const selectedRuleFilterLogEvents = useMemo(
        () => (
            viewMode === 'basic'
                ? filterLogEvents
                : selectedRouter && selectedRule
                    ? filterLogEvents.filter(e => (
                        String(e.router_id) === String(selectedRouter)
                        && String(e.rule_id) === String(selectedRule)
                    ))
                : filterLogEvents
        ),
        [viewMode, filterLogEvents, selectedRouter, selectedRule],
    );
    const selectedRouterMeta = useMemo(
        () => routers.find(router => String(router.id) === String(selectedRouter)) || null,
        [routers, selectedRouter],
    );
    const selectedTelemetryStatus = normalizeTelemetryStatus(selectedRouterMeta?.telemetry_status);
    const selectedTelemetryWarning = selectedRouter
        ? telemetryStatusMessage(selectedTelemetryStatus, selectedRouterMeta?.telemetry_last_seen)
        : null;
    const rulesUnavailable = selectedRouter
        && (selectedTelemetryStatus === 'offline' || selectedTelemetryStatus === 'no-data');
    const rulesEmptyMessage = selectedRouter
        ? rulesUnavailable
            ? 'Rule telemetry is currently unavailable for this router'
            : 'No rule data available'
        : 'Select a router to view rules';

    if (loading) {
        return (
            <div className="ddos-page">
                <div className="ddos-loading">
                    <span className="material-symbols-outlined ddos-spin" style={{ fontSize: '2rem', color: 'var(--color-accent-teal)' }}>sync</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ddos-page">
            {/* Hero */}
            <div className="ddos-hero">
                <div>
                    <div className="ddos-breadcrumb">
                        <span className="ddos-breadcrumb-dot" />
                        <span className="ddos-breadcrumb-text">Network Security</span>
                    </div>
                    <h1 className="ddos-title">DDoS Guard</h1>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {viewMode === 'detailed' && selectedRouter && (
                        <span
                            className={`ddos-telemetry-badge ${selectedTelemetryStatus}`}
                            title={selectedRouterMeta?.telemetry_last_seen
                                ? `Last telemetry update: ${formatTime(selectedRouterMeta.telemetry_last_seen)}`
                                : 'No telemetry timestamp available'}
                        >
                            Rules {telemetryStatusLabel(selectedTelemetryStatus)}
                        </span>
                    )}
                    <span className={`ddos-sse-badge ${sseConnected ? 'connected' : 'disconnected'}`}>
                        <span className={`ddos-online-dot ${sseConnected ? 'online' : 'offline'}`} />
                        {sseConnected ? 'Live' : 'Disconnected'}
                    </span>
                </div>
            </div>

            {error && (
                <div className="ddos-error-banner">
                    <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>error</span>
                    {error}
                    <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>close</span>
                    </button>
                </div>
            )}

            <div className="ddos-view-switch" role="tablist" aria-label="DDoS Guard view">
                <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'basic'}
                    className={`ddos-view-switch-btn${viewMode === 'basic' ? ' active' : ''}`}
                    onClick={() => handleViewModeChange('basic')}
                >
                    <span className="material-symbols-outlined">filter_alt</span>
                    Basic
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'detailed'}
                    className={`ddos-view-switch-btn${viewMode === 'detailed' ? ' active' : ''}`}
                    onClick={() => handleViewModeChange('detailed')}
                >
                    <span className="material-symbols-outlined">monitoring</span>
                    Detailed
                </button>
            </div>

            {viewMode === 'basic' ? (
                <div className="ddos-layout">
                    <div className="ddos-main">
                        <FiltersTable
                            filters={visibleFilters}
                            selectedRule={null}
                            deletingFilterKeys={deletingFilterKeys}
                            onDeleteFilter={handleDeleteFilter}
                            showRule={false}
                            emptyMessage="No active filters across this organization"
                        />

                        <HistoricalFiltersTable
                            events={selectedRuleFilterLogEvents}
                            selectedRule={null}
                            selectedRouter={null}
                            isAdmin={isAdmin}
                            deletingLogEntryKeys={deletingFilterLogEntryKeys}
                            onDeleteLogEntry={handleDeleteFilterLogEntry}
                            showRouter
                            showRule
                            emptyMessage="No filter log entries across this organization"
                        />
                    </div>
                </div>
            ) : (
            <>
            {/* Router selector */}
            <div className="ddos-router-selector">
                <span className="ddos-router-selector-label">Router</span>
                {routers.length === 0 ? (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>No routers found</span>
                ) : (() => {
                    const orgIds = [...new Set(routers.map(r => r.organization_id).filter(Boolean))];
                    const multiOrg = orgIds.length > 1;

                    if (!multiOrg) {
                        return routers.map((r, i) => (
                            <button
                                key={r.id ?? i}
                                className={`ddos-router-btn${selectedRouter === r.id ? ' active' : ''}`}
                                onClick={() => selectRouter(r.id)}
                            >
                                <span className={`ddos-online-dot ${r.live_status === 'online' ? 'online' : 'offline'}`} />
                                {r.id}
                                <span className="ddos-router-btn-version">v{r.version}</span>
                            </button>
                        ));
                    }

                    // Multi-org: group routers by organization_id
                    return orgIds.map((orgId, i) => (
                        <span key={orgId ?? i} className="ddos-router-org-group">
                            <span className="ddos-router-org-label">Org {orgId}</span>
                            {routers.filter(r => r.organization_id === orgId).map((r, j) => (
                                <button
                                    key={r.id ?? j}
                                    className={`ddos-router-btn${selectedRouter === r.id ? ' active' : ''}`}
                                    onClick={() => selectRouter(r.id)}
                                >
                                    <span className={`ddos-online-dot ${r.live_status === 'online' ? 'online' : 'offline'}`} />
                                    {r.id}
                                    <span className="ddos-router-btn-version">v{r.version}</span>
                                </button>
                            ))}
                        </span>
                    ));
                })()}
            </div>

            {routers.length === 0 ? (
                <div className="ddos-empty" style={{ marginTop: '3rem' }}>No routers found for this organization.</div>
            ) : (
            <div className="ddos-layout">
                {/* Main content */}
                <div className="ddos-main">
                    {/* Rules table */}
                    <div>
                        <div className="ddos-section-header">
                            <h2 className="ddos-section-title">
                                Rule Status {selectedRouter && <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>— {selectedRouter}</span>}
                            </h2>
                            <button className="ddos-icon-btn" onClick={() => selectedRouter && loadRules(selectedRouter)} title="Refresh rules">
                                <span className="material-symbols-outlined">refresh</span>
                            </button>
                        </div>
                        {selectedTelemetryWarning && (
                            <div className={`ddos-telemetry-warning ${selectedTelemetryStatus}`}>
                                <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>warning</span>
                                <span>{selectedTelemetryWarning}</span>
                            </div>
                        )}
                        {/* <p className="ddos-rules-hint">Row click selects a rule for graphs and logs.</p> */}
                        {rules.length === 0 || rulesUnavailable ? (
                            <div className="ddos-empty">
                                {rulesEmptyMessage}
                            </div>
                        ) : (
                            <RulesTable
                                rules={rules}
                                selectedRule={selectedRule}
                                onSelectRule={setSelectedRule}
                            />
                        )}
                    </div>

                    {/* Grafana panels for selected rule */}
                    {selectedRule && selectedRouter && grafanaReady && !rulesUnavailable && (
                        <GrafanaPanels
                            routerId={selectedRouter}
                            ruleId={selectedRule}
                        />
                    )}

                    {/* Active filters */}
                    <FiltersTable
                        filters={visibleFilters}
                        selectedRule={selectedRule}
                        deletingFilterKeys={deletingFilterKeys}
                        onDeleteFilter={handleDeleteFilter}
                    />

                    {/* Filter log */}
                    <HistoricalFiltersTable
                        events={selectedRuleFilterLogEvents}
                        selectedRule={selectedRule}
                        selectedRouter={selectedRouter}
                        isAdmin={isAdmin}
                        deletingLogs={deletingFilterLogs}
                        deletingLogEntryKeys={deletingFilterLogEntryKeys}
                        onDeleteLogs={handleDeleteFilterLogs}
                        onDeleteLogEntry={handleDeleteFilterLogEntry}
                    />

                    {/* Event log */}
                    <EventsTable
                        events={selectedRuleEvents}
                        selectedRule={selectedRule}
                        selectedRouter={selectedRouter}
                        isAdmin={isAdmin}
                        deletingLogs={deletingEventLogs}
                        onDeleteLogs={handleDeleteEventLogs}
                    />
                </div>
            </div>
            )}
            </>
            )}
        </div>
    );
}
